import React from 'react';

type ScriptLoaders<T> = {
    [K in keyof T]: () => Promise<T[K]>;
};
export enum LoadingStrategy {
    Hydrate = 'hydrate',
    Interaction = 'interaction',
    /**
     * Load only when the component is mounted inside an iframe
     * (i.e., `window.self !== window.top`). Useful for CMS live-preview
     * bundles that should never be shipped to end-users browsing the page
     * directly. No-op during SSR.
     */
    InIframe = 'in-iframe',
}

type UseDynamicImportsResult<T> = {
    hasLoaded: boolean;
    scripts: {
        [K in keyof T]: Awaited<Awaited<T[K]>>
    };
    load: () => Promise<void>
};

const useDynamicImports = <T>(scripts: ScriptLoaders<T>, strategy: LoadingStrategy): UseDynamicImportsResult<T> => {
    const [packageRefs, setPackageRefs] = React.useState<Partial<T>>({});
    const importsDepsUpdate = JSON.stringify(Object.keys(scripts));
    const hasLoaded: boolean = Object.keys(scripts).every(
        key => packageRefs[key as keyof T] !== undefined,
    );
    const loadImports = React.useCallback(async (): Promise<void> => {
        const clonedRefs = { ...packageRefs };
        await Promise.all(
            Object.entries(scripts).map(async ([key, loader]) => {
                const typedLoader = loader as () => Promise<unknown>;
                if (!clonedRefs[key as keyof T]) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const res: any = await typedLoader();
                    clonedRefs[key as keyof T] = (res?.default ?? res) as T[keyof T];
                }
            }),
        );
        setPackageRefs(clonedRefs);
        // eslint-disable-next-line
    }, [importsDepsUpdate]);

    React.useEffect(() => {
        if (strategy === LoadingStrategy.Hydrate) {
            loadImports();
            return;
        }
        if (strategy === LoadingStrategy.InIframe) {
            if (typeof window !== 'undefined' && window.self !== window.top) {
                loadImports();
            }
        }
    }, [loadImports, strategy]);

    return {
        hasLoaded,
        scripts: packageRefs as unknown as { [K in keyof T]: Awaited<Awaited<T[K]>> },
        load: loadImports,
    };
};

export default useDynamicImports;
